import { useState, useEffect } from "react";
import { formatEuro, parseEuro } from "@/components/AutoTextarea";

/**
 * EuroInput
 * ---------
 * Displays the price in German euro format (1.234,56) when not focused.
 * When the user focuses the field, switches to a plain numeric edit
 * (so they can type without fighting the separators), then re-formats on blur.
 * Calls onChange(numberOrNull) – never a string – so the caller stores a clean number.
 */
export function EuroInput({ value, onChange, testid, className = "", placeholder = "0,00", disabled = false }) {
  const [text, setText] = useState(formatEuro(value));
  const [focused, setFocused] = useState(false);

  // Stay in sync if the parent updates the value externally
  useEffect(() => {
    if (!focused) setText(formatEuro(value));
  }, [value, focused]);

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        data-testid={testid}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={disabled}
        onFocus={() => {
          if (disabled) return;
          setFocused(true);
          // Show the raw number (with comma as decimal separator) so it's editable
          const n = parseEuro(text);
          setText(n === null ? "" : String(n).replace(".", ","));
        }}
        onChange={(e) => {
          if (disabled) return;
          // Allow digits, comma and dot only – tolerate either separator while typing
          const v = e.target.value.replace(/[^\d.,]/g, "");
          setText(v);
          const n = parseEuro(v);
          onChange?.(n === null ? 0 : n);
        }}
        onBlur={() => {
          if (disabled) return;
          setFocused(false);
          const n = parseEuro(text);
          setText(n === null ? "" : formatEuro(n));
          onChange?.(n === null ? 0 : n);
        }}
        className={`text-right pr-7 ${disabled ? "opacity-60 cursor-not-allowed" : ""} ${className}`}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
    </div>
  );
}
